import { Component } from '@theme/component';

/**
 * Moves tab buttons into the purple sidebar, then wires selection + option pills.
 * Submit Inquiry appends selected options as URL query params.
 *
 * @extends {Component}
 */
class VerticalTabsComponent extends Component {
  /** @type {HTMLElement[]} */
  #tabs = [];

  /** @type {HTMLElement[]} */
  #panels = [];

  /** @type {number} */
  #active = 0;

  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    super.connectedCallback();
    this.#relocateTabs();
    if (!this.#tabs.length || !this.#panels.length) return;

    this.#active = Math.max(
      0,
      this.#tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')
    );

    this.#bindEvents();
    // scrollTab=false: never move the page/tablist on first paint
    this.#updateActiveTab(this.#active, false, false);
    // Do not scroll on initial connect — Element.scrollIntoView must never run (page jump).
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
  }

  /**
   * Tab buttons are rendered beside panels (block context = settings work),
   * then moved into the sidebar tablist so Theme Editor wrappers cannot hide them.
   */
  #relocateTabs() {
    const tablist = this.querySelector('[data-vertical-tabs-tablist]');
    const panels = [...this.querySelectorAll('[data-vertical-tab-panel]')];
    const buttons = [...this.querySelectorAll('[data-vertical-tab-button]')];

    this.#panels = panels;
    this.#tabs = [];

    if (!(tablist instanceof HTMLElement)) return;

    for (const button of buttons) {
      tablist.append(button);
      this.#tabs.push(button);
    }

    if (this.#tabs.length !== this.#panels.length && panels.length) {
      this.#tabs = buttons;
      this.#panels = panels;
    }
  }

  /**
   * Horizontally center the active tab inside the tablist only.
   * Never call Element.scrollIntoView — that scrolls the page and jumps
   * the homepage to Find Your Solution / Factory Story on load.
   *
   * @param {boolean} smooth
   */
  #scrollActiveTabIntoView(smooth = true) {
    const tab = this.#tabs[this.#active];
    const tablist = this.querySelector('[data-vertical-tabs-tablist]');
    if (!(tab instanceof HTMLElement) || !(tablist instanceof HTMLElement)) return;

    const maxScrollLeft = Math.max(0, tablist.scrollWidth - tablist.clientWidth);
    if (maxScrollLeft <= 0) return;

    const tabCenter = tab.offsetLeft + tab.offsetWidth / 2;
    const targetLeft = Math.min(
      maxScrollLeft,
      Math.max(0, tabCenter - tablist.clientWidth / 2)
    );

    tablist.scrollTo({
      left: targetLeft,
      behavior: smooth ? 'smooth' : 'auto',
    });
  }

  #bindEvents() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('keydown', this.#handleKeydown, { signal });

    for (const [index, tab] of this.#tabs.entries()) {
      tab.addEventListener('click', () => this.#updateActiveTab(index, true), { signal });
    }

    this.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const submit = target.closest('[data-vertical-tab-submit]');
        if (submit instanceof HTMLAnchorElement) {
          event.preventDefault();
          this.#navigateWithSelections(submit);
          return;
        }

        const option = target.closest('[data-vertical-tab-option]');
        if (!(option instanceof HTMLElement)) return;

        const group = option.closest('[data-vertical-tab-option-group]');
        if (!(group instanceof HTMLElement)) return;

        for (const item of group.querySelectorAll('[data-vertical-tab-option]')) {
          item.setAttribute('aria-pressed', item === option ? 'true' : 'false');
          item.classList.toggle('is-selected', item === option);
        }
      },
      { signal }
    );
  }

  /**
   * Build inquiry URL from selected option pills and navigate.
   * @param {HTMLAnchorElement} submit
   */
  #navigateWithSelections(submit) {
    const panel = submit.closest('[data-vertical-tab-panel]');
    const baseHref = submit.getAttribute('href') || '/pages/contact-us';
    let url;

    try {
      url = new URL(baseHref, window.location.origin);
    } catch {
      window.location.href = baseHref;
      return;
    }

    const type = submit.getAttribute('data-inquiry-type') || 'team';
    const typeLabel = submit.getAttribute('data-inquiry-type-label') || type;
    url.searchParams.set('type', type);
    url.searchParams.set('type_label', typeLabel);

    const root = panel instanceof HTMLElement ? panel : this;
    for (const group of root.querySelectorAll('[data-vertical-tab-option-group]')) {
      if (!(group instanceof HTMLElement)) continue;
      const key = group.getAttribute('data-param-key');
      if (!key) continue;

      const selected = group.querySelector('[data-vertical-tab-option].is-selected, [data-vertical-tab-option][aria-pressed="true"]');
      if (!(selected instanceof HTMLElement)) continue;

      const value = selected.getAttribute('data-option-value') || selected.textContent?.trim() || '';
      const label = selected.getAttribute('data-option-label') || selected.textContent?.trim() || value;
      const groupLabel = group.getAttribute('data-param-label') || key;

      if (value) url.searchParams.set(key, value);
      if (label) url.searchParams.set(`${key}_label`, label);
      if (groupLabel) url.searchParams.set(`${key}_name`, groupLabel);
    }

    if (submit.target === '_blank') {
      window.open(url.toString(), '_blank', 'noopener,noreferrer');
    } else {
      window.location.href = url.toString();
    }
  }

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    const currentIndex = this.#tabs.findIndex((tab) => tab === document.activeElement);
    if (currentIndex < 0) return;

    let nextIndex = currentIndex;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % this.#tabs.length;
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + this.#tabs.length) % this.#tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.#tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#updateActiveTab(nextIndex, true);
    this.#tabs[nextIndex]?.focus({ preventScroll: true });
  };

  /**
   * @param {number} index
   * @param {boolean} focusPanel
   * @param {boolean} [scrollTab]
   */
  #updateActiveTab(index, focusPanel = false, scrollTab = true) {
    if (!this.#tabs[index] || !this.#panels[index]) return;

    this.#active = index;

    for (const [i, tab] of this.#tabs.entries()) {
      const selected = i === index;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.setAttribute('tabindex', selected ? '0' : '-1');
      tab.classList.toggle('is-active', selected);
    }

    for (const [i, panel] of this.#panels.entries()) {
      const selected = i === index;
      panel.classList.toggle('is-active', selected);
      panel.setAttribute('aria-hidden', selected ? 'false' : 'true');
      panel.tabIndex = selected ? 0 : -1;
      if (selected) {
        panel.removeAttribute('inert');
      } else {
        panel.setAttribute('inert', '');
      }
    }

    if (focusPanel) {
      this.#panels[index]?.focus?.({ preventScroll: true });
    }

    if (scrollTab) {
      this.#scrollActiveTabIntoView(true);
    }
  }
}

if (!customElements.get('vertical-tabs-component')) {
  customElements.define('vertical-tabs-component', VerticalTabsComponent);
}
